import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import { useStyletron } from 'parkdepot-shared/theme'
import React, { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import useLanguage from '@hooks/useLanguage.hook'
import { StatefulCalendar } from 'baseui/datepicker'
import { de, enGB } from 'date-fns/locale'
import { CheckoutContext } from '../../context/CheckoutContext/checkout.context'
import useBreakpoint from '../../hooks/useBreakpoint'
import { AdyenResultStatus } from '../../types/adyenResultStatus.enum'
import { LotViewName } from '../../types/lot.interface'
import { lotRoute, lotRouteLongDuration, lotRouteNightDuration, lotRouteShortDuration } from '../../views/Lot/route'
import Text from '../style/Text.style'
import {
    ButtonContainer,
    CalendarContainer,
    ContentContainer,
    ErrorIcon,
    FullyBookedContainer,
    TextWrapper
} from './PaymentError.style'

interface Props {
    paymentStatus: AdyenResultStatus | undefined
    onBackClick: () => void
    unAvailableDates?: string[]
}

const getLocale = (i18nLanguage: string) => {
    if (i18nLanguage.includes('en')) return enGB
    return de
}

// date: example: Wed Feb 19 2025 00:00:00 GMT+0100
// unavailableDates: example: ["2025-02-18T00:00:00+01:00"]
const isDateUnavailable = (date: Date, unavailableDates: string[]) => {
    // Convert the input date to YYYY-MM-DD format (ignoring time)
    const formattedDate = date.toISOString().split('T')[0]

    // Check if formattedDate exists in unavailableDates
    return unavailableDates.some((unavailableDate) => unavailableDate.split('T')[0] === formattedDate)
}

const PaymentError: FC<Props> = ({ paymentStatus, onBackClick, unAvailableDates }) => {
    const breakpoint = useBreakpoint()
    const { t } = useTranslation()
    const [, theme] = useStyletron()
    const history = useHistory()
    const { checkout } = useContext(CheckoutContext)
    const [lng] = useLanguage()

    const isMobile = breakpoint <= 1
    const unavailable = unAvailableDates && unAvailableDates.length > 0

    const onBtnClick = () => {
        onBackClick()
    }

    const onUnavailableBtnClick = () => {
        if (checkout?.lotCheckoutType) {
            if (checkout?.lotCheckoutType === LotViewName.NIGHTLY) {
                history.replace({ pathname: lotRouteNightDuration(checkout?.lot?.id) })
            } else if (checkout?.lotCheckoutType === LotViewName.DAILY) {
                history.replace({ pathname: lotRouteLongDuration(checkout?.lot?.id) })
            } else if (checkout?.lotCheckoutType === LotViewName.HOURLY) {
                history.replace({ pathname: lotRouteShortDuration(checkout?.lot?.id) })
            }
        } else {
            history.push(`${lotRoute(checkout?.lot?.id)}`)
        }
    }

    return (
        <div>
            <ContentContainer>
                <ErrorIcon className={unavailable ? 'pkd-mood-sad' : 'pkd-robot-1'} />
                <FullyBookedContainer>
                    <Text typography={theme.typography.HeadingXSmall} fontWeight={600}>
                        {t('errors.checkout.title')}
                    </Text>
                </FullyBookedContainer>
                {unavailable ? (
                    <>
                        <div>
                            <Text typography={theme.typography.ParagraphMedium} fontWeight={400} $align="center">
                                {t('errors.checkout.unavailable.description')}
                            </Text>
                            <TextWrapper>
                                <Text typography={theme.typography.ParagraphMedium} fontWeight={400} $align="center">
                                    {t('errors.checkout.unavailable.dateDescription')}
                                </Text>
                            </TextWrapper>
                            <CalendarContainer className="pd-range-datepicker">
                                <StatefulCalendar
                                    filterDate={(date) => !isDateUnavailable(date, unAvailableDates)} // Disable unavailable dates
                                    minDate={new Date()}
                                    locale={getLocale(lng)}
                                    overrides={{
                                        Root: {
                                            style: () => ({
                                                backgroundColor: '#fff',
                                                display: 'contents'
                                            })
                                        },
                                        CalendarContainer: {
                                            style: ({ $theme }) => ({
                                                ...$theme.typography.ParagraphSmall,
                                                pointerEvents: 'none',
                                                backgroundColor: '#fff',
                                                borderBottomLeftRadius: '2.4rem',
                                                borderBottomRightRadius: '2.4rem'
                                            })
                                        },
                                        CalendarHeader: {
                                            style: () => ({
                                                color: '#ADADAD',
                                                borderTopLeftRadius: '2.4rem',
                                                borderTopRightRadius: '2.4rem',
                                                width: '100%'
                                            })
                                        },
                                        WeekdayHeader: {
                                            style: () => ({
                                                color: '#ADADAD'
                                            })
                                        },
                                        NextButton: {
                                            style: ({ $theme }) => ({
                                                color: $theme.colors.primary
                                            })
                                        },
                                        PrevButton: {
                                            style: ({ $theme }) => ({
                                                color: $theme.colors.primary
                                            })
                                        },
                                        MonthYearSelectButton: {},
                                        MonthYearSelectIconContainer: {
                                            style: () => ({
                                                display: 'none'
                                            })
                                        },
                                        Day: {
                                            style: ({
                                                $theme,
                                                $selected,
                                                $isHighlighted,
                                                $date,
                                                $disabled,
                                                $outsideMonth
                                            }) => {
                                                const beforeToday = $date < new Date()

                                                return {
                                                    color: beforeToday ? '#ADADAD !important' : '#000 !important',
                                                    ':before': {
                                                        ...(!$outsideMonth && {
                                                            backgroundColor: '#EFF3FE !important'
                                                        })
                                                    },
                                                    ':after': {
                                                        backgroundColor: '#fff'
                                                    },
                                                    ...(($isHighlighted || $selected) && {
                                                        color: '#fff',
                                                        ':after': {
                                                            backgroundColor: $theme.colors.primary,
                                                            color: '#fff',
                                                            borderTopWidth: '0',
                                                            borderRightWidth: '0',
                                                            borderBottomWidth: '0',
                                                            borderLeftWidth: '0'
                                                        },

                                                        ':before': {
                                                            backgroundColor: '#eff3fe !important'
                                                        }
                                                    }),
                                                    ...($disabled && {
                                                        color: '#ADADAD !important',
                                                        textDecoration: 'line-through'
                                                    })
                                                }
                                            }
                                        }
                                    }}
                                />
                            </CalendarContainer>
                        </div>
                        <ButtonContainer>
                            <Button
                                id="unavailable-btn"
                                onClick={onUnavailableBtnClick}
                                $size={BUTTON_SIZE.medium}
                                overrides={{
                                    BaseButton: {
                                        style: {
                                            width: '100%'
                                        }
                                    }
                                }}
                            >
                                {t('errors.checkout.unavailable.buttonText')}
                            </Button>
                        </ButtonContainer>
                    </>
                ) : (
                    <>
                        <div>
                            <Text typography={theme.typography.ParagraphMedium} fontWeight={400}>
                                {paymentStatus === AdyenResultStatus.REFUSED && t('errors.checkout.description')}
                                {paymentStatus !== AdyenResultStatus.REFUSED && t('errors.checkout.description2')}
                            </Text>
                        </div>
                        <ButtonContainer>
                            <Button
                                id="payment-error-btn"
                                onClick={onBtnClick}
                                $size={BUTTON_SIZE.medium}
                                overrides={{
                                    BaseButton: {
                                        style: {
                                            width: '100%'
                                        }
                                    }
                                }}
                            >
                                {paymentStatus === AdyenResultStatus.REFUSED &&
                                    t('errors.checkout.chooseMeansOfPayment')}
                                {paymentStatus !== AdyenResultStatus.REFUSED && t('errors.checkout.repeatPayment')}
                            </Button>
                        </ButtonContainer>
                    </>
                )}
            </ContentContainer>
        </div>
    )
}

export default PaymentError
